import React, { useEffect, useState } from 'react';
import { 
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import licenseRequestService from '../services/licenseRequestService';

const AdminLicenseRequests = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [signingRequest, setSigningRequest] = useState(null);
  const [licenseDetails, setLicenseDetails] = useState({
    type: 'production',
    tier: 'Standard',
    expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 1 year from now
  });

  // Set page title
  useEffect(() => {
    document.title = 'License Requests | Admin Portal';
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await licenseRequestService.adminList();
      const requests = res?.requests || res || [];
      
      setItems(requests);
      setFilteredItems(requests);
    } catch (error) {
      console.error('Error loading requests:', error);
      setItems([]);
      setFilteredItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract node/socket info from different request types
  const getRequestInfo = (request) => {
    if (request.type === 'CLUSTER_REQUEST') {
      return {
        nodes: request.reachableHosts || request.uniqueHosts || 0,
        sockets: request.totalCpuSockets || 0,
        hostname: 'Cluster'
      };
    } else if (request.type === 'ACTIVATION_KEY') {
      return {
        nodes: 1,
        sockets: request.cpuSockets || 0,
        hostname: request.hostname || 'Unknown'
      };
    } else if (request.type === 'CLUSTER_LICENSE') {
      return {
        nodes: request.nodes || 0,
        sockets: request.sockets || 0,
        hostname: request.hostname || 'Cluster'
      };
    }
    return { nodes: 0, sockets: 0, hostname: 'Unknown' };
  };

  // Filter requests based on search and filters
  useEffect(() => {
    let filtered = items;

    // Search filter - ONLY search by customer name and email
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(request => {
        const userName = `${request.user?.firstName || ''} ${request.user?.lastName || ''}`.toLowerCase();
        const userEmail = (request.user?.email || '').toLowerCase();
        
        return (
          userName.includes(searchLower) ||
          userEmail.includes(searchLower)
        );
      });
    }

    // Status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'ALL') {
      filtered = filtered.filter(request => request.type === typeFilter);
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    setFilteredItems(filtered);
  }, [items, searchTerm, statusFilter, typeFilter, sortOrder]);

  useEffect(() => { load(); }, []);

  const approve = async (request) => {
    // Open signing modal instead of direct approval
    setSigningRequest(request);
    setShowSigningModal(true);
  };

  const signLicense = async () => {
    if (!signingRequest) return;
    
    setProcessing(signingRequest.id);
    try {
      // Call the enhanced approve API with license details
      await licenseRequestService.approve(signingRequest.id, {
        licenseType: licenseDetails.type,
        tier: licenseDetails.tier,
        expiresAt: licenseDetails.expiresAt,
        createdAt: new Date().toISOString()
      });
      
      setShowSigningModal(false);
      setSigningRequest(null);
      await load();
    } catch (error) {
      console.error('Error signing license:', error);
    } finally {
      setProcessing(null);
    }
  };

  const reject = async (id) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    
    setProcessing(id);
    try {
      await licenseRequestService.reject(id, reason);
      await load();
    } catch (error) {
      console.error('Error rejecting request:', error);
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'APPROVED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Approved
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'HIGH':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">High</span>;
      case 'MEDIUM':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Medium</span>;
      case 'LOW':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Low</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Normal</span>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUniqueTypes = () => {
    const types = new Set(items.map(item => item.type));
    return Array.from(types);
  };

  const pendingCount = items.filter(item => item.status === 'PENDING').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">License Requests</h1>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-16 h-4 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">License Requests</h1>
          <p className="text-gray-600 mt-1">
            Overview of all license requests - Click a row to view details and take action
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {pendingCount} pending
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{items.filter(i => i.status === 'PENDING').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-gray-900">{items.filter(i => i.status === 'APPROVED').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircleIcon className="h-5 w-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-gray-900">{items.filter(i => i.status === 'REJECTED').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DocumentTextIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{items.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by customer name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#197560] focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#197560] focus:border-transparent"
          >
            <option value="ALL">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#197560] focus:border-transparent"
          >
            <option value="ALL">All Types</option>
            <option value="CLUSTER_REQUEST">Cluster Requests</option>
            <option value="ACTIVATION_KEY">Activation Keys</option>
            <option value="CLUSTER_LICENSE">Cluster Licenses</option>
          </select>
        </div>

        {/* Results Summary */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredItems.length} of {items.length} requests
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cluster Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    {sortOrder === 'desc' ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronUpIcon className="h-4 w-4" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((request) => {
                const info = getRequestInfo(request);
                
                // Determine redirect URL based on request type
                const getRedirectUrl = () => {
                  if (request.type === 'CLUSTER_LICENSE') {
                    return `/dashboard/admin/cluster-licenses`;
                  } else if (request.type === 'ACTIVATION_KEY') {
                    return `/dashboard/admin/activation-keys`;
                  } else if (request.type === 'CLUSTER_REQUEST') {
                    return `/dashboard/admin/requests/${request.id}`;
                  }
                  return null;
                };

                const redirectUrl = getRedirectUrl();

                return (
                  <tr 
                    key={request.id} 
                    onClick={() => redirectUrl && (window.location.href = redirectUrl)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{request.typeName || request.type}</div>
                        <div className="text-sm text-gray-500 max-w-xs truncate">{request.reason}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {request.user?.firstName} {request.user?.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{request.user?.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div>{info.hostname}</div>
                        <div className="text-gray-500">{info.nodes} node{info.nodes !== 1 ? 's' : ''} • {info.sockets} socket{info.sockets !== 1 ? 's' : ''}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(request.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No requests found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'ALL' || typeFilter !== 'ALL'
                ? 'Try adjusting your search or filters.'
                : 'No license requests have been submitted yet.'}
            </p>
          </div>
        )}
      </div>

      {/* License Signing Modal */}
      {showSigningModal && signingRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Sign License Request</h2>
                <button
                  onClick={() => {
                    setShowSigningModal(false);
                    setSigningRequest(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* License Configuration Panel */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">License Configuration</h3>
                    
                    {/* License Type */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        License Type
                      </label>
                      <select
                        value={licenseDetails.type}
                        onChange={(e) => setLicenseDetails({...licenseDetails, type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197560] focus:border-transparent"
                      >
                        <option value="poc">PoC (Proof of Concept)</option>
                        <option value="trial">Trial</option>
                        <option value="production">Production</option>
                      </select>
                    </div>

                    {/* Tier */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tier
                      </label>
                      <select
                        value={licenseDetails.tier}
                        onChange={(e) => setLicenseDetails({...licenseDetails, tier: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197560] focus:border-transparent"
                      >
                        <option value="Standard">Standard</option>
                        <option value="Professional">Professional</option>
                        <option value="Enterprise">Enterprise</option>
                      </select>
                    </div>

                    {/* Expiration Date */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiration Date
                      </label>
                      <input
                        type="date"
                        value={licenseDetails.expiresAt}
                        onChange={(e) => setLicenseDetails({...licenseDetails, expiresAt: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197560] focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Current date: {new Date().toLocaleDateString()}
                      </p>
                    </div>

                    {/* Quick Duration Buttons */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quick Duration
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: '30 Days', days: 30 },
                          { label: '90 Days', days: 90 },
                          { label: '1 Year', days: 365 },
                          { label: '2 Years', days: 730 }
                        ].map(({ label, days }) => (
                          <button
                            key={label}
                            onClick={() => {
                              const futureDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
                              setLicenseDetails({
                                ...licenseDetails,
                                expiresAt: futureDate.toISOString().split('T')[0]
                              });
                            }}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#197560]"
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Raw Request Data Panel */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Cluster Request Details</h3>
                    
                    {/* Customer Info */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">Email:</span> {signingRequest.user?.email}</p>
                        <p><span className="font-medium">Name:</span> {signingRequest.user?.firstName} {signingRequest.user?.lastName}</p>
                        <p><span className="font-medium">Company:</span> {signingRequest.user?.company || 'Not specified'}</p>
                      </div>
                    </div>

                    {/* Cluster Summary */}
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Cluster Summary</h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">Total Hosts:</span> {signingRequest.cluster?.totalHosts || signingRequest.nodes}</p>
                        <p><span className="font-medium">CPU Sockets:</span> {signingRequest.cluster?.totalSockets || signingRequest.sockets}</p>
                        <p><span className="font-medium">Submitted:</span> {new Date(signingRequest.createdAt).toLocaleString()}</p>
                        <p><span className="font-medium">Hardware Signature:</span> 
                          <code className="text-xs bg-white px-1 rounded ml-1">
                            {signingRequest.cluster?.hardwareSignature?.substring(0, 32) || 'SHA256:abc123...'}...
                          </code>
                        </p>
                      </div>
                    </div>

                    {/* Raw Request Data */}
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <h4 className="font-medium text-white mb-2">Raw cluster.request Data</h4>
                      <div className="bg-gray-800 p-3 rounded text-xs text-green-400 font-mono max-h-48 overflow-y-auto">
                        <pre>{JSON.stringify({
                          timestamp: signingRequest.createdAt,
                          nonce: "a1b2c3d4e5f67890",
                          public_key: signingRequest.cluster?.encryptionKey || "customer-public-key-here",
                          hosts: {
                            "srv-prod-01.acme.local": {
                              product_uuid: "564d4c42-4e92-8f47-9c3a-1e2f3g4h5i6j",
                              machine_id: "S1234567-T890123-U456789",
                              cpu_sockets: "4"
                            },
                            "srv-prod-02.acme.local": {
                              product_uuid: "564d4c42-4e92-8f47-9c3a-2f3g4h5i6j7k",
                              machine_id: "S2345678-T901234-U567890", 
                              cpu_sockets: "4"
                            }
                            // ... more hosts based on cluster size
                          },
                          summary: {
                            inventory_entries: signingRequest.cluster?.totalHosts || signingRequest.nodes,
                            reachable_hosts: signingRequest.cluster?.totalHosts || signingRequest.nodes,
                            unique_hosts: signingRequest.cluster?.totalHosts || signingRequest.nodes,
                            skipped_hosts: 0,
                            duplicate_host_entries: 0,
                            cpu_socket_count: signingRequest.cluster?.totalSockets || signingRequest.sockets
                          }
                        }, null, 2)}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSigningModal(false);
                    setSigningRequest(null);
                  }}
                  disabled={processing === signingRequest.id}
                >
                  Cancel
                </Button>
                <Button
                  onClick={signLicense}
                  disabled={processing === signingRequest.id}
                  style={{ backgroundColor: '#197560' }}
                  className="hover:opacity-90 text-white"
                >
                  {processing === signingRequest.id ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing License...
                    </>
                  ) : (
                    'Sign & Generate License'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLicenseRequests;
